import {
  ActionType,
  extractMentionsFromText,
  getMentionTextParts,
  IssueCreationSource,
  IssueType,
  Media,
  MediaPickerComponent,
  MediaType,
  parseMMDDYYYYToDate,
  PickerMode,
  ReportIssueInput,
  Severity,
  TriggerType,
  useCreateIssueWithMessage,
  useCreateMutation,
  useCurrentLocation,
  useWorkspaceLabels,
} from '@gripp/shared-logic';

import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { InferType } from 'yup';
import { Layout } from '../../styles';
import { Colors } from '../../themes';
import { SaveFormTop } from '../forms';
import { KeyboardSafeView } from '../keyboard/keyboardSafeView';
import { HorizontalMediaGallery } from '../media';
import { TopComponent } from '../navigation';
import {
  NewIssueReportForm,
  NewIssueReportFormSchema,
} from './newIssueReportForm';

const CREATE_WORKFLOW = `
  mutation createWorkflow($input: CreateWorkflowInput!) {
    createWorkflow(input: $input) {
      id
      name
    }
  }
`;

const CREATE_LABEL = `
  mutation CreateLabel($input: CreateLabelInput!) {
    createLabel(input: $input) {
      id
      name
    }
  }
`;

type NewIssueProps = {
  assetId?: string;
  workspaceId: string;
  mediaPicker: MediaPickerComponent;
  acceptedMediaTypes?: MediaType[];
  onIssueCreated: (id: string) => void;
  onCancel?: () => void;
  topBar: TopComponent;
  mode?: PickerMode;
  createdFrom?: IssueCreationSource;
  hideAdvancedSettings?: boolean;
  pickerMode?: 'picker' | 'camera';
};

type NewIssueReportFormData = InferType<typeof NewIssueReportFormSchema>;

export const NewIssue = (props: NewIssueProps) => {
  const { assetId, workspaceId, createdFrom } = props;
  const MediaPicker = props.mediaPicker;
  const { mutateAsync, isPending } = useCreateIssueWithMessage();
  const { getLocation } = useCurrentLocation();
  const { t } = useTranslation();
  const [issueMedia, setIssueMedia] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch labels from current workspace
  const { labels: availableLabels } = useWorkspaceLabels(workspaceId);

  // Create label mutation
  const { mutateAsync: createLabel } = useCreateMutation({
    modelName: 'label',
    query: CREATE_LABEL,
  });

  const handleCreateLabel = async (name: string) => {
    try {
      const result = await createLabel({
        input: { name, workspace: { id: workspaceId } },
      });
      return result;
    } catch (error) {
      console.error('Failed to create label:', error);
      throw error;
    }
  };

  const { mutateAsync: createWorkflow } = useCreateMutation({
    modelName: 'workflow',
    query: CREATE_WORKFLOW,
  });

  const issueTypeTitle = t('issue.new.addIt');

  const addMedia = (newMediaItem: Media) => {
    if (!newMediaItem) return;
    setIssueMedia((prevState) => [...prevState, newMediaItem.id]);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (formData: NewIssueReportFormData) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const location = await getLocation();

      // Map severity to appropriate issue type
      const getIssueTypeFromSeverity = (severity: Severity): IssueType => {
        switch (severity) {
          case Severity.Red:
          case Severity.Yellow:
          case Severity.Green:
            return IssueType.Report;
          case Severity.Purple:
            return IssueType.Note;
          default:
            return IssueType.Report;
        }
      };

      const mentions = extractMentionsFromText(formData.message);

      // Extract first 4 words from message as fallback title
      const extractTitleFromMessage = (message: string): string => {
        if (!message) return '';

        const parts = getMentionTextParts(message);
        const tokens: string[] = [];

        parts.forEach((part) => {
          if (part.isMention) {
            tokens.push(part.text);
          } else {
            tokens.push(...part.text.split(' ').filter((w) => w.trim()));
          }
        });

        return tokens.slice(0, 4).join(' ');
      };

      const input: ReportIssueInput = {
        severity: formData.activityType as Severity,
        title: formData.title || extractTitleFromMessage(formData.message),
        type: getIssueTypeFromSeverity(formData.activityType as Severity),
        message: formData.message,
        workspaceId: workspaceId,
        media: issueMedia.length > 0 ? issueMedia : undefined,
        location,
        createdFrom,
        labelIds: formData.labels?.map((label) => label.id),
        userMentions:
          mentions.length > 0 ? mentions.map((m) => m.id as string) : undefined,
        adjustCreationDate: formData.adjustCreationDate
          ? parseMMDDYYYYToDate(String(formData.adjustCreationDate))
          : undefined,
      };

      if (assetId) input.assetId = assetId;

      await mutateAsync(
        { input },
        {
          onSettled: async (data: {
            issue: { reportIssue: { id: string } };
          }) => {
            props.onIssueCreated(data.issue.reportIssue.id);

            if (formData.quickReminderDate) {
              const parsedDate = parseMMDDYYYYToDate(
                String(formData.quickReminderDate)
              );
              const workflowInput = {
                name: `${t('issue.reminderFor')} ${
                  formData.title || formData.message
                }`,
                workspace: { id: workspaceId },
                trigger: {
                  type: TriggerType.Issue,
                  config: {
                    issueId: data.issue.reportIssue.id,
                    onDate: parsedDate,
                  },
                },
                action: {
                  type: ActionType.Reminder,
                },
              };

              await createWorkflow({ input: workflowInput });
            }
          },
        }
      );
    } catch (error) {
      // Handle submission errors silently
    } finally {
      setIsSubmitting(false);
    }
  };

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isDirty, isValid },
  } = useForm({
    resolver: yupResolver(NewIssueReportFormSchema),
    mode: 'onChange',
    defaultValues: {
      message: '',
      title: '',
      activityType: Severity.Red, // Default to Urgent
      labels: [],
      quickReminderDate: undefined,
      changeDisplayDate: undefined,
      adjustCreationDate: undefined,
    },
  });

  return (
    <KeyboardSafeView style={styles.container}>
      <SaveFormTop
        isDirty={isDirty && !isPending}
        isValid={isValid && !isUploading && !isPending && !isSubmitting}
        onSubmit={async () => {
          await handleSubmit(onSubmit)();
        }}
        title={issueTypeTitle}
        saveLabel={t('issue.new.submit')}
        topBar={props.topBar}
        onCancel={props.onCancel}
      >
        <NewIssueReportForm
          workspaceId={workspaceId}
          mediaGallery={HorizontalMediaGallery}
          control={control}
          setValue={setValue}
          watch={watch}
          mediaPicker={MediaPicker}
          addMedia={addMedia}
          setIsUploading={setIsUploading}
          acceptedMediaTypes={props.acceptedMediaTypes}
          availableLabels={availableLabels}
          onCreateLabel={handleCreateLabel}
          hideAdvancedSettings={props.hideAdvancedSettings}
          pickerMode={props.pickerMode}
        />
      </SaveFormTop>
    </KeyboardSafeView>
  );
};

const styles = StyleSheet.create({
  container: {
    ...Layout.container,
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 12,
  },
});
